/**
 * Identité visuelle (Mijoté).
 *
 * Trois pièces, une seule source : la marque (marmite, vapeur, basilic), le
 * mot « Mijoté » et la baseline « Mes recettes, tout simplement ». Elles sont
 * découpées du logo maître par `scripts/build-brand-assets.py` ; ce fichier
 * est le seul endroit de l'application qui connaît leurs chemins et leurs
 * proportions.
 *
 * **Le mot est une image, la baseline est du texte.** Le lettrage de « Mijoté »
 * (grotesque géométrique arrondie, feuille de basilic sur le « é ») n'est pas
 * une des deux polices du design system : le composer en Fraunces ou en Inter
 * donnerait un autre mot. La baseline, elle, est un texte courant en capitales
 * espacées — la reproduire en image la rendrait floue au zoom, invisible à la
 * recherche et muette pour un lecteur d'écran, sans rien gagner.
 *
 * **Deux fichiers pour le mot, pas un.** Le lettrage est un gris quasi noir,
 * illisible sur le fond sombre. Une variante crème existe ; le basculement se
 * fait en CSS (`.brand-word-*` dans index.css) et non par `prefers-color-scheme`
 * seul, pour suivre aussi le réglage explicite de thème (US-24) — une image
 * choisie par requête média ignorerait le choix « Clair » fait sur un système
 * en sombre.
 */

/** Proportions natives des fichiers générés — évitent tout saut de mise en page au chargement. */
const MARK_SIZE = 128;
const WORD_WIDTH = 720;
const WORD_HEIGHT = 237;

export interface LogoMarkProps {
  /** Côté du carré, en pixels. */
  size?: number;
  className?: string;
}

/**
 * Marque seule. Décorative par défaut (`alt=""`) : elle accompagne toujours un
 * texte qui porte déjà le sens, dans l'en-tête comme dans les états vides.
 */
export function LogoMark({ size = 32, className }: LogoMarkProps): React.JSX.Element {
  return (
    <img
      src="/brand/mijote-mark.png"
      srcSet="/brand/mijote-mark.png 1x, /brand/mijote-mark@2x.png 2x"
      width={MARK_SIZE}
      height={MARK_SIZE}
      style={{ width: size, height: size }}
      alt=""
      aria-hidden="true"
      decoding="async"
      className={className}
    />
  );
}

export interface LogoWordProps {
  /** Hauteur du lettrage, en pixels. La largeur suit. */
  height?: number;
  /**
   * Nom accessible. `Mijoté` par défaut ; passer une chaîne vide quand le mot
   * est déjà annoncé à côté (lockup dans un titre, par exemple).
   */
  alt?: string;
}

/** Le mot « Mijoté », dans sa version claire ou sombre selon le thème actif. */
export function LogoWord({ height = 24, alt = 'Mijoté' }: LogoWordProps): React.JSX.Element {
  const width = Math.round((WORD_WIDTH / WORD_HEIGHT) * height);
  const common = {
    width: WORD_WIDTH,
    height: WORD_HEIGHT,
    style: { width, height },
    alt,
    decoding: 'async' as const,
  };
  return (
    <>
      {/* Les deux portent le même `alt` : celle qui n'est pas du thème actif est
          en `display: none`, donc ni affichée ni annoncée. Poser `aria-hidden`
          sur l'une des deux priverait de nom accessible le thème correspondant. */}
      <img src="/brand/mijote-wordmark.png" className="brand-word-light" {...common} />
      <img src="/brand/mijote-wordmark-dark.png" className="brand-word-dark" {...common} />
    </>
  );
}

/**
 * Signature horizontale : marque + mot. C'est la forme de l'en-tête, où la
 * baseline serait du bruit — elle se lit une fois, à l'arrivée, pas à chaque
 * écran.
 */
export function LogoInline({ height = 26 }: { height?: number }): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-2.5 align-middle">
      <LogoMark size={Math.round(height * 1.35)} />
      <LogoWord height={height} />
    </span>
  );
}

/**
 * Signature verticale complète, baseline comprise. Réservée aux écrans où
 * l'application se présente : la connexion, et le pied de page d'une recette
 * partagée — les deux seuls endroits où quelqu'un peut ne pas savoir ce qu'il
 * a sous les yeux.
 */
export function LogoLockup({
  markSize = 88,
  wordHeight = 40,
  tagline = true,
}: {
  markSize?: number;
  wordHeight?: number;
  tagline?: boolean;
}): React.JSX.Element {
  return (
    <span className="flex flex-col items-center gap-3">
      <LogoMark size={markSize} />
      <span className="flex items-center">
        <LogoWord height={wordHeight} />
      </span>
      {tagline && (
        <span className="flex items-center gap-2.5 text-[0.6875rem] font-medium uppercase tracking-[0.2em] text-foreground-secondary">
          {/* Les deux traits encadrant la baseline appartiennent au logo. Ils
              reprennent la primaire, qui est désormais l'orange de la marmite. */}
          <span aria-hidden="true" className="h-0.5 w-5 rounded-full bg-primary" />
          Mes recettes, tout simplement
          <span aria-hidden="true" className="h-0.5 w-5 rounded-full bg-primary" />
        </span>
      )}
    </span>
  );
}
